import { Component, computed, effect, inject, resource, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule, type ControlValueAccessor } from '@angular/forms';
import { AutoFocusDirective } from '../shared/auto-focus/auto-focus.directive';
import { PermissionRequiredDirective } from '../shared/authentication/permission-required.directive';
import { ResendDocument } from './services/tracking-shared-action-methods.service';
import { TrackingService } from './services/tracking.service';

import { ToggleButton, ToggleButtonItem } from '../controls/togglebutton';

@Component({
    templateUrl: './tracking-resend-modal.component.html',
    styleUrls: ['./tracking-resend-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, AutoFocusDirective, PermissionRequiredDirective, ToggleButton]
})

export class TrackingResendModalComponent {

    readonly MESSAGE_KEY = 'Message';
    readonly INTERCHANGE_KEY = 'Interchange';

    readonly INTERCHANGE_BATCH_TEXT = 'Selection(s) is part of a document batch. All related documents will be resent, when resending on interchange level.';
    readonly MISSING_MESSAGE_IN_TEXT = 'Selection(s) is missing Message-In file(s) and will be resent on interchange level.';
    readonly MISSING_MESSAGE_IN_BATCH_TEXT = 'Selection(s) with missing Message-In files will be resent at the interchange level. If the interchange is a document batch, all related documents will be resent as well.';
    readonly INTERCHANGES_WITH_NO_DOCUMENTS_TEXT = 'Selection(s) is in a state that requires resend on interchange level.';

    resendDocuments: ResendDocument[] = [];
    selectedRows = 1;

    toggleButton = viewChild.required<Pick<ControlValueAccessor, 'setDisabledState'>>('toggleButton');

    activeModal = inject(NgbActiveModal);
    trackingService = inject(TrackingService);

    resendResult = signal<TrackingResendModalResult>(new TrackingResendModalResult());
    focusTriggerChange = signal<number>(new Date().getMilliseconds());
    resendByInterchange = signal<boolean>(false);

    selectedButtons = signal<string[]>([]);
    buttons: ToggleButtonItem[] = [
        {
          key: 'Message',
          text: 'Resend on message level',
          icon: '',
          hint: ''
        },
        {
          key: 'Interchange',
          text: 'Resend on interchange level',
          icon: '',
          hint: ''
        }
    ];

    resendDocumentResultResource = resource({
        params: () => ({ resendDocuments: this.resendDocuments }),
        loader: ({ params }) => this.fetchResendDocumentResults(params.resendDocuments)
    });
    resendDocumentResult = computed(() => this.resendDocumentResultResource.value() ?? []);

    documentCount = computed(() => this.calculateDocumentCount(this.resendDocuments, this.resendDocumentResult()).documentids.length);
    interchangesWithNoDocumens = computed(() => this.calculateDocumentCount(this.resendDocuments, this.resendDocumentResult()).interchangesWithNoDocumens);
    interchangesMissingMessageIn = computed(() => this.calculateDocumentCount(this.resendDocuments, this.resendDocumentResult()).interchangesMissingMessageIn);

    onlyInterchangesWithNoDocumentsAndMissingMessageIn = computed(() =>
        this.anyInterchangesWithNoDocumentsAndMissingMessageIn(
            this.interchangesWithNoDocumens().size,
            this.interchangesMissingMessageIn().size,
            this.documentCount()
        )
    );

    interchangesWithOneDocument = computed(() => this.calculateInterchangeCount().interchangesWithOneDocumentCount);
    interchangesWithMultipleDocuments = computed(() => this.calculateInterchangeCount().interchangesWithMultipleDocuments);

    warningTexts = computed(() => this.triggerInformationAndWarning(this.interchangesWithNoDocumens().size, this.interchangesMissingMessageIn().size).warnings);
    informationTexts = computed(() => this.triggerInformationAndWarning(this.interchangesWithNoDocumens().size, this.interchangesMissingMessageIn().size).infos);

    totalInterchangeSelections = computed(() => this.interchangesWithNoDocumens().size + this.interchangesMissingMessageIn().size);

    constructor() {
        effect(() => {
            if(this.onlyInterchangesWithNoDocumentsAndMissingMessageIn()){
                this.toggleButton()?.setDisabledState?.(true);
                this.selectedButtons.set([this.INTERCHANGE_KEY]);
                this.resendByInterchange.set(true);
            }else{
                this.selectedButtons.set([this.MESSAGE_KEY]);
                this.resendByInterchange.set(false);
            }
        });
    }

    // Modal Actions
    ok() {
        this.resendResult.set({
            resendByInterchange: this.resendByInterchange(),
            resendGenerateNewTransmissionId: this.resendResult().resendGenerateNewTransmissionId,
            comment: this.resendResult().comment
        });

        this.activeModal.close(this.resendResult());
    }

    close() {
        this.activeModal.dismiss();
    }

    anyInterchangesWithNoDocumentsAndMissingMessageIn(interchangesWithNoDocumensCount: number, missingMessageInCount: number, documentCount: number): boolean {
        const value = ((interchangesWithNoDocumensCount > 0 || missingMessageInCount > 0) && documentCount === 0);
        return value;
    }

    onUseCustomChange(value: string[]): void {
        const defaultButton = this.selectedButtons().includes(this.INTERCHANGE_KEY) ? this.INTERCHANGE_KEY : this.MESSAGE_KEY;
        this.selectedButtons.set(value ?? [defaultButton]);
        this.resendByInterchange.set(this.selectedButtons().includes(this.INTERCHANGE_KEY));
    }

    triggerInformationAndWarning(interchangesWithNoDocumensCount: number, missingMessageInCount: number): { warnings: string[]; infos: string[] } {
        const infos: string[] = [];
        const warnings: string[] = [];

        if (this.resendByInterchange()) {
            if (this.resendDocumentResult().length > this.selectedRows) {
                warnings.push(this.interchangesWithMultipleDocuments() + ' ' + this.INTERCHANGE_BATCH_TEXT);
            }
        }

        if (interchangesWithNoDocumensCount > 0) {
            infos.push(interchangesWithNoDocumensCount + ' ' + this.INTERCHANGES_WITH_NO_DOCUMENTS_TEXT);
        }

        if (missingMessageInCount > 0 && this.resendDocumentResult().length > this.selectedRows) {
            infos.push(missingMessageInCount + ' ' + this.MISSING_MESSAGE_IN_BATCH_TEXT);
        } else if (missingMessageInCount > 0) {
            infos.push(missingMessageInCount + ' ' + this.MISSING_MESSAGE_IN_TEXT);
        }
        return { warnings, infos };
    }

    private async fetchResendDocumentResults(resendDocuments: ResendDocument[]): Promise<TrackingResendDocumentResult[]> {
        const interchangeIds = this.extractInterchangeIds(resendDocuments);
        return await this.trackingService.GetResendDocumentResultByInterchangeIds(Array.from(interchangeIds));
    }

    private calculateDocumentCount(resendDocuments: ResendDocument[], resendDocumentResult: TrackingResendDocumentResult[]): { documentids: number[]; interchangesWithNoDocumens: Set<number>; interchangesMissingMessageIn: Set<number> } {
        const documentids: number[] = [];
        const interchangesWithNoDocumens = new Set<number>();
        const interchangesMissingMessageIn = new Set<number>();

        if(resendDocumentResult.length === 0){
            return { documentids: [], interchangesWithNoDocumens: new Set(), interchangesMissingMessageIn: new Set() };
        }

        resendDocuments.forEach(doc => {
            if (!doc.documentId) {
                interchangesWithNoDocumens.add(doc.interchangeInId);
            } else if (this.missingMessageIn(doc, resendDocumentResult)) {
                interchangesMissingMessageIn.add(doc.interchangeInId);
            } else {
                documentids.push(doc.documentId);
            }
        });
        return { documentids, interchangesWithNoDocumens, interchangesMissingMessageIn };
    }

    private calculateInterchangeCount(): { interchangesWithOneDocumentCount: number; interchangesWithMultipleDocuments: number } {
        const interchangeGroups = new Map<number, number>();

        // Group documents by InterchangeInId and count them
        this.resendDocumentResult().forEach(doc => {
            const count = interchangeGroups.get(doc.InterchangeInId) || 0;
            interchangeGroups.set(doc.InterchangeInId, count + 1);
        });

        let interchangesWithOneDocumentCount = 0;
        let interchangesWithMultipleDocuments = 0;

        // Count interchanges with one document and multiple documents
        interchangeGroups.forEach((count, interchangeId) => {
            if (count === 1) {
                interchangesWithOneDocumentCount++;
            } else if (count > 1) {
                // Count only the selected documents part of the interchange
                const selectedDocumentCount = this.resendDocuments.filter(
                    doc => doc.interchangeInId === interchangeId
                ).length;

                interchangesWithMultipleDocuments += selectedDocumentCount;
            }
        });

        return { interchangesWithOneDocumentCount, interchangesWithMultipleDocuments };
    }

    // Helpers
    private extractInterchangeIds(resendDocuments: ResendDocument[]): Set<number> {
        return new Set(resendDocuments.map(doc => doc.interchangeInId));
    }

    private missingMessageIn(doc: ResendDocument, resendDocumentResult: TrackingResendDocumentResult[]): boolean {
        return resendDocumentResult.some(
            result => result.MissingMessageIn && result.InterchangeInId === doc.interchangeInId
        );
    }
}

export class TrackingResendModalResult {
    resendByInterchange: boolean = false;
    resendGenerateNewTransmissionId: boolean = false;
    comment: string = '';
}

export class TrackingResendDocumentResult {
    InterchangeInId: number;
    DocumentId?: number;
    MissingMessageIn: boolean = false;
}
